// Workout type definitions. Display labels + color accents for the week canvas.

// Per-type accent colour for the athlete-facing day-row pill chip + icon.
// Distinct from the `color` field above (which is the legacy planner-grid
// tint). User's spec: recovery=blue, easy=green, tempo/race=yellow,
// threshold/speed=red. Other types use natural greens/burgundy/navy.
// `icon` is a lucide-react component name resolved at render time.
export const WORKOUT_TYPES = [
  { value: "easy",      label: "Easy run",         color: "#92C58A", group: "run",   accent: "#2F855A", icon: "Footprints" },
  { value: "long",      label: "Long run",         color: "#5E8A56", group: "run",   accent: "#225E3B", icon: "Footprints" },
  { value: "recovery",  label: "Recovery run",     color: "#B7D4E0", group: "run",   accent: "#3A6FA1", icon: "Footprints" },
  { value: "tempo",     label: "Tempo",            color: "#E6A35A", group: "run",   accent: "#B97816", icon: "Footprints" },
  { value: "threshold", label: "Threshold / LT",   color: "#D4822C", group: "run",   accent: "#B5371E", icon: "Footprints" },
  { value: "speed",     label: "Speed / Intervals",color: "#C85545", group: "run",   accent: "#B5371E", icon: "Zap" },
  { value: "race_pace", label: "Race pace",        color: "#8E4D42", group: "run",   accent: "#B97816", icon: "Flag" },
  { value: "race",      label: "Race / Event",     color: "#7A1F2B", group: "run",   accent: "#7A1F2B", icon: "Trophy" },
  { value: "walk",      label: "Walk",             color: "#A8C5A2", group: "run",   accent: "#5C6F4E", icon: "Footprints" },
  { value: "hike",      label: "Hike",             color: "#7A9670", group: "run",   accent: "#5C6F4E", icon: "Mountain" },
  { value: "strength",  label: "Strength",         color: "#6B5D8A", group: "other", accent: "#4C3F66", icon: "Dumbbell" },
  { value: "cross",     label: "Cross-training",   color: "#8A7C6B", group: "other", accent: "#4C3F66", icon: "Bike" },
  { value: "mobility",  label: "Mobility",         color: "#7AA9B7", group: "other", accent: "#3A6FA1", icon: "PersonStanding" },
  { value: "other",     label: "Other",            color: "#A8A099", group: "other", accent: "#6F6A66", icon: "Activity" },
  { value: "rest",      label: "Rest",             color: "#D6D0C6", group: "other", accent: "#6F6A66", icon: "Moon" },
];

export const WORKOUT_TYPE_MAP = Object.fromEntries(
  WORKOUT_TYPES.map((t) => [t.value, t])
);

// Workouts that are logged by status/RPE/note only — no distance or duration
// fields. Covers explicit non-cardio types, anything built from strength
// blocks (without run segments), and untyped / cross-training sessions whose
// title clearly reads as a lift ("Lower Body B", "Upper Body A", "Gym").
const STRENGTH_TITLE_RE = /\b(strength|lift(?:ing)?|weights?|gym|upper[\s-]?body|lower[\s-]?body|full[\s-]?body|core|circuit|mobility|yoga|pilates)\b/i;
export function isStrengthLikeWorkout(workout) {
  if (!workout) return false;
  const t = workout.type || "";
  if (["strength", "mobility", "other"].includes(t)) return true;
  const hasBlocks = Array.isArray(workout.strength_blocks) && workout.strength_blocks.length > 0;
  const hasSegments = Array.isArray(workout.segments) && workout.segments.length > 0;
  if (hasBlocks && !hasSegments) return true;
  if ((!t || t === "cross") && STRENGTH_TITLE_RE.test(workout.title || "")) return true;
  return false;
}

export function workoutTypeLabel(value) {
  return WORKOUT_TYPE_MAP[value]?.label || value;
}

export function workoutTypeColor(value) {
  // Prefer the new shared accent colour so coach planner tiles match the
  // athlete-portal tile palette (recovery=blue, easy=green, tempo=yellow,
  // threshold/speed=red, strength=navy-purple, etc.). Falls back to the
  // legacy `color` (and finally a neutral) for safety.
  const t = WORKOUT_TYPE_MAP[value];
  return t?.accent || t?.color || "#D6D0C6";
}

// Zone options
export const ZONE_OPTIONS = [1, 2, 3, 4, 5];

// Zone → RPE mapping (Natalie's convention)
export const ZONE_RPE_MAP = {
  1: [1, 2],
  2: [2, 4],
  3: [5, 6],
  4: [7, 8],
  5: [9, 10],
};

// Given a list of selected zones, return [rpeMin, rpeMax].
// For multi-zone selections (e.g. Z1-2), we widen to cover all selected zones.
export function zonesToRpe(zones) {
  if (!zones || zones.length === 0) return [null, null];
  let min = Infinity;
  let max = -Infinity;
  for (const z of zones) {
    const r = ZONE_RPE_MAP[z];
    if (!r) continue;
    if (r[0] < min) min = r[0];
    if (r[1] > max) max = r[1];
  }
  if (min === Infinity) return [null, null];
  return [min, max];
}

// Unit dropdown choices
export const UNIT_OPTIONS = [
  { value: "min", label: "min" },
  { value: "sec", label: "sec" },
  { value: "hr", label: "hr" },
  { value: "km", label: "km" },
  { value: "m", label: "m" },
  { value: "miles", label: "mi" },
];

// ---- Segment factories & defaults per workout type ----

function newId() {
  return `seg-${Math.random().toString(36).slice(2, 10)}`;
}

export function mkSimple(overrides = {}) {
  const zones = overrides.zones || [];
  const [rmin, rmax] = overrides.rpe_min != null || overrides.rpe_max != null
    ? [overrides.rpe_min ?? null, overrides.rpe_max ?? null]
    : zonesToRpe(zones);
  return {
    id: newId(),
    kind: "simple",
    label: "",
    value: "",
    unit: "min",
    zones: [],
    rpe_min: null,
    rpe_max: null,
    pace: "",
    incline: "",
    notes: "",
    ...overrides,
    rpe_min: rmin,
    rpe_max: rmax,
  };
}

export function mkGroup(overrides = {}) {
  return {
    id: newId(),
    kind: "group",
    label: "Main Set",
    repeat_count: 6,
    items: [],
    ...overrides,
  };
}

// Segment templates shared across defaults
export function mkWarmup({ value = "10", unit = "min", zones = [1] } = {}) {
  return mkSimple({ label: "Warm-up", value, unit, zones });
}

export function mkCooldown({ value = "10", unit = "min", zones = [1] } = {}) {
  return mkSimple({ label: "Cool-down", value, unit, zones });
}

export function mkWork(overrides = {}) {
  return mkSimple({ label: "Work", value: "", unit: "min", zones: [4], ...overrides });
}

export function mkRest(overrides = {}) {
  return mkSimple({ label: "Rest", value: "", unit: "min", zones: [1], ...overrides });
}

/**
 * Parse free-text like "20km", "8 km tempo", "60 min", "1.5h" to a single primary
 * quantity ({kind: 'km'|'min', value: number}). Returns null if nothing recognized.
 */
export function parseTextQuantity(text) {
  if (!text) return null;
  const lower = String(text).toLowerCase();
  const km = lower.match(/(\d+(?:\.\d+)?)\s*(km|kilometers|kilometres)\b/);
  if (km) return { kind: "km", value: parseFloat(km[1]) };
  const kShort = lower.match(/(\d+(?:\.\d+)?)k(?!m)(?![a-z])/);
  if (kShort) return { kind: "km", value: parseFloat(kShort[1]) };
  const mi = lower.match(/(\d+(?:\.\d+)?)\s*(mi|miles|mile)\b/);
  if (mi) return { kind: "km", value: parseFloat(mi[1]) * 1.609344 };
  const hr = lower.match(/(\d+(?:\.\d+)?)\s*(hr|hour|hours|h)\b/);
  if (hr) return { kind: "min", value: parseFloat(hr[1]) * 60 };
  const min = lower.match(/(\d+(?:\.\d+)?)\s*(min|minutes|mins)\b/);
  if (min) return { kind: "min", value: parseFloat(min[1]) };
  return null;
}

function _round1(v) {
  return Math.round(v * 10) / 10;
}

/**
 * Build smarter default segments by reading the workout's free-text
 * `duration_or_distance` (and `title`). Examples:
 *   "60 min easy"   → 5 min Z1 wu · 50 min Z2 · 5 min Z1 cd
 *   "30 min easy"   → 5 min Z1 wu · 20 min Z2 · 5 min Z1 cd
 *   "20 km easy"    → 20 km Z2 (with note: first/last 5 min Z1 wu/cd)
 *   "8 km tempo"    → 10 min Z1 wu · 8 km Z4 tempo · 10 min Z1 cd
 *   no quantity     → blank main set + Z1 wu/cd (so coach can fill in)
 */
export function smartSegmentsFor(type, workout) {
  const text = `${workout?.duration_or_distance || ""} ${workout?.title || ""}`.trim();
  const q = parseTextQuantity(text);
  const wuZones = type === "speed" ? [1, 2] : [1];

  // No quantity yet → blank-out the main set instead of 30/90 min default
  if (!q) {
    if (type === "easy") {
      return [
        mkWarmup({ value: "5", zones: wuZones }),
        mkSimple({ label: "Main Set", value: "", unit: "min", zones: [2] }),
        mkCooldown({ value: "5", zones: [1] }),
      ];
    }
    if (type === "long") {
      return [
        mkWarmup({ value: "10", zones: wuZones }),
        mkSimple({ label: "Main Set", value: "", unit: "min", zones: [2] }),
        mkCooldown({ value: "10", zones: [1] }),
      ];
    }
    return defaultSegmentsFor(type);
  }

  // Time-based easy / recovery → ONE segment for the full duration.
  // Coach preference: "45 min easy" stays "45 min in Z2" — no wu/cd split.
  if ((type === "easy" || type === "recovery") && q.kind === "min") {
    const zone = type === "recovery" ? 1 : 2;
    const label = type === "recovery" ? "Recovery run" : "Easy run";
    return [
      mkSimple({ label, value: String(_round1(q.value)), unit: "min", zones: [zone] }),
    ];
  }

  // Time-based long → split duration into wu / main / cd
  if (type === "long" && q.kind === "min") {
    const wu = 10;
    const cd = 10;
    const main = Math.max(0, q.value - wu - cd);
    return [
      mkWarmup({ value: String(wu), zones: wuZones }),
      mkSimple({
        label: "Main Set",
        value: main > 0 ? String(_round1(main)) : "",
        unit: "min",
        zones: [2],
      }),
      mkCooldown({ value: String(cd), zones: [1] }),
    ];
  }

  // Distance-based easy / long / recovery → single segment, with wu/cd in notes
  if (q.kind === "km" && (type === "easy" || type === "long" || type === "recovery")) {
    const zone = type === "recovery" ? 1 : 2;
    const label =
      type === "long" ? "Long run" : type === "recovery" ? "Recovery run" : "Easy run";
    return [
      mkSimple({
        label,
        value: String(_round1(q.value)),
        unit: "km",
        zones: [zone],
        notes: "First 5 min Z1 (warm-up) · Last 5 min Z1 (cool-down)",
      }),
    ];
  }

  // Distance-based tempo / threshold / race-pace → wu + work + cd
  if (q.kind === "km" && type === "tempo") {
    return [
      mkSimple({
        label: "Main Set",
        value: String(_round1(q.value)),
        unit: "km",
        zones: [3],
        notes:
          "Spend the first 5–10 min in Z1–Z2 to warm up, and the last 5 min in Z1–Z2 to cool down.",
      }),
    ];
  }
  if (q.kind === "km" && type === "threshold") {
    return [
      mkWarmup({ value: "10", zones: [1] }),
      mkSimple({ label: "Threshold", value: String(_round1(q.value)), unit: "km", zones: [4] }),
      mkCooldown({ value: "10", zones: [1] }),
    ];
  }
  if (q.kind === "km" && type === "race_pace") {
    return [
      mkWarmup({ value: "10", zones: [1] }),
      mkSimple({ label: "Race pace", value: String(_round1(q.value)), unit: "km", zones: [3] }),
      mkCooldown({ value: "10", zones: [1] }),
    ];
  }

  // Race
  if (type === "race" && q.kind === "km") {
    return [mkSimple({ label: "Race", value: String(_round1(q.value)), unit: "km", zones: [] })];
  }

  // Anything else → fall through to existing defaults
  return defaultSegmentsFor(type);
}

export function defaultSegmentsFor(type) {
  switch (type) {
    case "easy":
      return [
        mkWarmup({ value: "5", zones: [1] }),
        mkSimple({ label: "Main Set", value: "30", unit: "min", zones: [2] }),
        mkCooldown({ value: "5", zones: [1] }),
      ];
    case "long":
      return [
        mkWarmup({ value: "10", zones: [1] }),
        mkSimple({ label: "Main Set", value: "90", unit: "min", zones: [2] }),
        mkCooldown({ value: "10", zones: [1] }),
      ];
    case "recovery":
      return [
        mkSimple({ label: "Recovery run", value: "30", unit: "min", zones: [1] }),
      ];
    case "tempo":
      return [
        mkWarmup({ value: "10", zones: [1] }),
        mkGroup({
          label: "Tempo intervals",
          repeat_count: 3,
          items: [
            mkSimple({ label: "Tempo", value: "10", unit: "min", zones: [4] }),
            mkSimple({ label: "Recovery", value: "2", unit: "min", zones: [3] }),
          ],
        }),
        mkCooldown({ value: "10", zones: [1] }),
      ];
    case "threshold":
      return [
        mkWarmup({ value: "10", zones: [1] }),
        mkGroup({
          label: "Threshold",
          repeat_count: 4,
          items: [
            mkSimple({ label: "Work", value: "5", unit: "min", zones: [4] }),
            mkSimple({ label: "Rest", value: "90", unit: "sec", zones: [1] }),
          ],
        }),
        mkCooldown({ value: "10", zones: [1] }),
      ];
    case "speed":
      return [
        mkWarmup({ value: "10", zones: [1, 2] }),
        mkGroup({
          label: "Main Set",
          repeat_count: 6,
          items: [
            mkSimple({ label: "Work", value: "2", unit: "min", zones: [4] }),
            mkSimple({ label: "Rest", value: "3", unit: "min", zones: [1] }),
          ],
        }),
        mkCooldown({ value: "10", zones: [1] }),
      ];
    case "race_pace":
      return [
        mkWarmup({ value: "10", zones: [1] }),
        mkSimple({ label: "Race pace", value: "", unit: "km", zones: [3] }),
        mkCooldown({ value: "10", zones: [1] }),
      ];
    case "race":
      return [mkSimple({ label: "Race", value: "", unit: "km", zones: [] })];
    case "strength":
      return [mkSimple({ label: "Strength session", value: "45", unit: "min" })];
    case "cross":
      return [mkSimple({ label: "Cross-training", value: "45", unit: "min" })];
    case "rest":
      return [];
    default:
      return [];
  }
}

export const SEGMENT_LABEL_OPTIONS = [
  "Warm-up",
  "Main Set",
  "Work",
  "Rest",
  "Tempo",
  "Recovery",
  "Surge",
  "Cruise",
  "Hill",
  "Cool-down",
  "Strength",
  "Custom",
];

/**
 * Given a program start_date (ISO "YYYY-MM-DD") and a 1-indexed weekNumber,
 * return [startDate, endDate] ISO strings for that week. Weeks are 7 days each.
 * Returns [null, null] if no start_date.
 */
export function weekDateRange(startDateIso, weekNumber) {
  if (!startDateIso || !weekNumber) return [null, null];
  const base = new Date(startDateIso + "T00:00:00");
  if (isNaN(base.getTime())) return [null, null];
  const start = new Date(base);
  start.setDate(base.getDate() + (weekNumber - 1) * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return [fmt(start), fmt(end)];
}

export function formatDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatDateRange(startIso, endIso) {
  const s = formatDateShort(startIso);
  const e = formatDateShort(endIso);
  if (!s && !e) return "";
  if (s && e) return `${s} – ${e}`;
  return s || e;
}

export function formatSegmentValue(seg) {
  if (!seg) return "";
  const parts = [];
  if (seg.value) parts.push(`${seg.value} ${seg.unit || "min"}`);
  const zones = (seg.zones || []).length ? `Z${seg.zones.join("/")}` : "";
  const rpe =
    seg.rpe_min || seg.rpe_max
      ? seg.rpe_min === seg.rpe_max
        ? `RPE ${seg.rpe_min}`
        : `RPE ${seg.rpe_min ?? ""}-${seg.rpe_max ?? ""}`
      : "";
  const meta = [zones, rpe].filter(Boolean).join(" · ");
  if (meta) parts.push(meta);
  if (seg.pace) parts.push(`@ ${seg.pace}`);
  if (seg.incline) parts.push(`${seg.incline}% incline`);
  return parts.join(" ");
}

// ---- Volume (auto-sum) helpers ----

const MIN_UNITS = new Set(["min", "sec", "hr"]);
const KM_UNITS = new Set(["km", "m", "miles"]);

function toMinutes(value, unit) {
  const v = parseFloat(value);
  if (isNaN(v)) return 0;
  if (unit === "sec") return v / 60;
  if (unit === "hr") return v * 60;
  return v; // default min
}

function toKilometers(value, unit) {
  const v = parseFloat(value);
  if (isNaN(v)) return 0;
  if (unit === "m") return v / 1000;
  if (unit === "miles") return v * 1.609344;
  return v; // default km
}

/**
 * Parse a free-text duration_or_distance string like "60 min" / "10 km" / "1.5 hr".
 * Also accepts compact forms like "5K", "6k", "10 K", "13.1mi", "1500m".
 * Returns { minutes, km } (both possible, 0 if not applicable).
 */
function parseDurationOrDistance(text) {
  if (!text || typeof text !== "string") return { minutes: 0, km: 0 };
  const trimmed = text.trim().toLowerCase();
  // Order matters: match more specific units (km, mi, hr) before shorter
  // ambiguous letters (m for metres, h for hours). The "k" alias matches
  // compact "5k" forms; "m" is only counted as metres when followed by a
  // word boundary (so "5 m" works but "5 min" still maps to minutes via the
  // earlier pattern).
  const patterns = [
    { re: /(\d+(?:\.\d+)?)\s*(hours|hour|hrs|hr|h)\b/, kind: "hr" },
    { re: /(\d+(?:\.\d+)?)\s*(minutes|minute|mins|min)\b/, kind: "min" },
    { re: /(\d+(?:\.\d+)?)\s*(seconds|second|secs|sec|s)\b/, kind: "sec" },
    { re: /(\d+(?:\.\d+)?)\s*(kilometers|kilometres|kms|km|k)\b/, kind: "km" },
    { re: /(\d+(?:\.\d+)?)\s*(miles|mile|mi)\b/, kind: "miles" },
    { re: /(\d+(?:\.\d+)?)\s*m\b/, kind: "m" },
  ];
  let minutes = 0;
  let km = 0;
  for (const { re, kind } of patterns) {
    const m = trimmed.match(re);
    if (!m) continue;
    if (MIN_UNITS.has(kind)) minutes += toMinutes(m[1], kind);
    else if (KM_UNITS.has(kind)) km += toKilometers(m[1], kind);
  }
  return { minutes, km };
}

// Common race-distance aliases. Coaches frequently type "Half", "Marathon",
// "5K", "10K", or just a bare number like "28" (meaning 28 km).
const RACE_DISTANCE_ALIASES = {
  "marathon": 42.195,
  "full": 42.195,
  "half": 21.0975,
  "half marathon": 21.0975,
  "10k": 10,
  "5k": 5,
  "ultra": 50, // conservative default for an unspecified ultra
};

/**
 * Parse a race's free-text distance string into { km, minutes }.
 *
 * Smarter than `parseDurationOrDistance` for races because:
 *   - Treats a bare number ("28") as kilometres so the coach can just type
 *     the distance without remembering the unit.
 *   - Recognises common race aliases ("Half", "Marathon", etc.).
 *   - Estimates a race-pace time: prefers the athlete's marathon-pace zone
 *     when supplied, falls back to a sensible 5:30/km default.
 *
 * `text` is searched first; if no distance can be derived from text but the
 * `name` is a recognised alias, that's used instead.
 */
export function parseRaceDistance(text, athletePaces) {
  const blob = (text || "").toString().trim().toLowerCase();
  if (!blob) return { km: 0, minutes: 0 };
  // 1) Aliases ("Half", "Marathon", "10k", ...)
  if (RACE_DISTANCE_ALIASES[blob] != null) {
    return _withRaceMinutes(RACE_DISTANCE_ALIASES[blob], athletePaces);
  }
  // 2) Try the standard parser (handles "10 km", "13.1 mi", "5K", etc.)
  const parsed = parseDurationOrDistance(blob);
  if (parsed.km > 0) return _withRaceMinutes(parsed.km, athletePaces);
  // 3) Bare number — treat as km so the coach can type just "28".
  const bare = blob.match(/^(\d+(?:\.\d+)?)$/);
  if (bare) {
    const km = parseFloat(bare[1]);
    if (km > 0) return _withRaceMinutes(km, athletePaces);
  }
  // 4) Fallback: number anywhere in the string, treat as km.
  const anywhere = blob.match(/(\d+(?:\.\d+)?)/);
  if (anywhere) {
    const km = parseFloat(anywhere[1]);
    if (km > 0) return _withRaceMinutes(km, athletePaces);
  }
  return { km: 0, minutes: 0 };
}

function _withRaceMinutes(km, athletePaces) {
  // Prefer marathon pace from the athlete's profile (best proxy for sustained
  // race effort). Fall back to 5:30/km (330 sec/km) — a generous estimate for
  // most amateur 5K-to-ultra distances.
  let secPerKm = 330;
  if (athletePaces && athletePaces.marathon) {
    const m = athletePaces.marathon;
    secPerKm = (m.slow_sec_per_km + m.fast_sec_per_km) / 2;
  }
  return { km, minutes: (km * secPerKm) / 60 };
}

/**
 * Pick a seconds-per-km value from a list of zone numbers (e.g. [1] for
 * easy, [4] for threshold/interval). Mid-zone average across the supplied
 * zones. Falls back to `fallbackSec` when paces aren't available or zones
 * don't map cleanly.
 *
 * This is what makes per-segment volume realistic. The old behaviour used
 * the workout's overall `type` for every minute — so a Speed workout's
 * warm-up + recovery jogs got priced at interval pace, wildly inflating km.
 */
function _secPerKmForZones(zones, paces, fallbackSec) {
  if (!paces || !Array.isArray(zones) || zones.length === 0) return fallbackSec;
  // Map each zone number → the closest training-pace bucket on the athlete's
  // pace profile. Z1-2 ≈ easy, Z2 ≈ marathon, Z3 ≈ tempo/threshold,
  // Z4 ≈ threshold, Z5 ≈ interval/speed.
  const bucketForZone = (z) => {
    if (z <= 1) return paces.easy;
    if (z === 2) return paces.marathon || paces.easy;
    if (z === 3) return paces.threshold || paces.marathon;
    if (z === 4) return paces.threshold || paces.interval;
    return paces.interval || paces.threshold;
  };
  let total = 0;
  let n = 0;
  for (const z of zones) {
    const b = bucketForZone(Number(z));
    if (b && b.slow_sec_per_km && b.fast_sec_per_km) {
      total += (b.slow_sec_per_km + b.fast_sec_per_km) / 2;
      n += 1;
    }
  }
  return n > 0 ? total / n : fallbackSec;
}

/**
 * Per-segment volume, zone-aware.
 *
 * Returns BOTH `km` and `minutes` for every running-type segment by
 * converting whichever side is missing using the segment's own zones.
 *
 * - "10 min in Z1" (easy ~7:30/km) → ~1.3 km + 10 min
 * - "2 min in Z4" (threshold ~5:30/km) → ~0.36 km + 2 min
 * - "1 km in Z1" → 1 km + ~7.5 min
 *
 * For "group" segments (repeats), the inner totals multiply by repeat_count.
 *
 * `paces` and `workoutTypeFallbackSec` are optional. When paces aren't
 * available we fall back to a sensible default (matches old behaviour).
 */
function segmentVolume(seg, paces = null, workoutTypeFallbackSec = 360) {
  if (!seg) return { minutes: 0, km: 0 };
  if (seg.kind === "group") {
    const inner = (seg.items || []).reduce(
      (acc, s) => {
        const v = segmentVolume(s, paces, workoutTypeFallbackSec);
        return { minutes: acc.minutes + v.minutes, km: acc.km + v.km };
      },
      { minutes: 0, km: 0 }
    );
    const rounds = Math.max(1, Number(seg.repeat_count) || 1);
    return { minutes: inner.minutes * rounds, km: inner.km * rounds };
  }
  const unit = (seg.unit || "min").toLowerCase();
  const secPerKm = _secPerKmForZones(seg.zones || [], paces, workoutTypeFallbackSec);
  if (MIN_UNITS.has(unit)) {
    const minutes = toMinutes(seg.value, unit);
    const km = secPerKm > 0 ? (minutes * 60) / secPerKm : 0;
    return { minutes, km };
  }
  if (KM_UNITS.has(unit)) {
    const km = toKilometers(seg.value, unit);
    const minutes = secPerKm > 0 ? (km * secPerKm) / 60 : 0;
    return { minutes, km };
  }
  return { minutes: 0, km: 0 };
}

// Workout types that translate to running km when only minutes are
// prescribed. Strength/mobility/rest stay at 0 km — they're not running.
export const RUNNING_TYPES = new Set([
  "easy",
  "long",
  "recovery",
  "tempo",
  "marathon",
  "speed",
  "interval",
  "intervals",
  "threshold",
  "race",
  "race_pace",
  "run",
  "strength_run",
]);
// Internal alias kept so the existing references in this file don't need
// renaming; external consumers should use the exported `RUNNING_TYPES`.
const _RUNNING_TYPES = RUNNING_TYPES;

// Default seconds-per-km by workout type when we don't have the athlete's
// derived training paces. Slow for easy/long/recovery, faster for tempo and
// speed work. Used to estimate km from a minutes-only prescription so the
// weekly mileage totals don't silently undercount.
const _DEFAULT_SEC_PER_KM = {
  easy: 360,
  long: 360,
  recovery: 390,
  run: 360,
  strength_run: 360,
  tempo: 300,
  marathon: 300,
  threshold: 285,
  speed: 240,
  interval: 240,
  intervals: 240,
  race: 270,
};

/**
 * Pick a seconds-per-km value for converting time-based workouts into an
 * estimated km. Prefers the athlete's derived training paces when supplied
 * (mid-zone), falls back to a sensible per-type default.
 */
function _secPerKmForType(type, paces) {
  const t = (type || "").toLowerCase();
  if (paces) {
    let zone = null;
    if (t === "easy" || t === "recovery" || t === "run" || t === "strength_run") zone = paces.easy;
    else if (t === "long") zone = paces.easy;
    else if (t === "tempo" || t === "marathon") zone = paces.marathon || paces.threshold;
    else if (t === "threshold") zone = paces.threshold;
    else if (t === "speed" || t === "interval" || t === "intervals") zone = paces.interval;
    else if (t === "race") zone = paces.marathon;
    if (zone && zone.slow_sec_per_km && zone.fast_sec_per_km) {
      return (zone.slow_sec_per_km + zone.fast_sec_per_km) / 2;
    }
  }
  return _DEFAULT_SEC_PER_KM[t] || 360;
}

/**
 * Total running volume for a workout.
 *
 * Coach-intent: the headline `duration_or_distance` shown on the card is the
 * source of truth for the prescribed total when both km and minutes are stated.
 * But when a coach builds intervals via the segment editor and only sketches a
 * brief headline (or none), the segment breakdown should pick up the slack.
 *
 * Resolution rule (component-wise max):
 *   km      = max(km parsed from headline, sum of segment km)
 *   minutes = max(min parsed from headline, sum of segment min)
 *
 * Time → km estimation:
 *   If a running-type workout ends up with `minutes > 0` and `km == 0` (i.e.
 *   the coach prescribed "60 min easy" with no distance), convert minutes
 *   to estimated km using `paces` if supplied, otherwise a sensible default
 *   pace per workout type. This stops weekly-mileage totals from silently
 *   ignoring time-based easy/long runs (the exact undercount Natalie hit
 *   on her portal: 15 / 17 km when the real prescribed was ~32 km).
 *
 * Example results:
 *   - headline "5K"           + intervals (0 km, 30 min)  → 5 km, 30 min
 *   - headline "60 min"       + segments (8 km, 0 min)    → 8 km, 60 min
 *   - headline "60 min easy"  + segments (0 km, 0 min)    → ~10 km, 60 min
 *   - headline "Speed 6×400m" + segments (2.4 km, 18 min) → 2.4 km, 18 min
 *   - headline empty          + segments (5 km, 25 min)   → 5 km, 25 min
 */
export function workoutVolume(workout, paces = null) {
  if (!workout || workout.type === "rest") return { minutes: 0, km: 0 };
  const headline = parseDurationOrDistance(workout.duration_or_distance || "");
  const segs = workout.segments || [];
  const isRunning = _RUNNING_TYPES.has((workout.type || "").toLowerCase());
  // The fallback pace used when a segment has no zones — e.g. a coach typed
  // "10 min" without picking a zone. We pass the workout's overall type pace
  // so behaviour matches the pre-zone-aware path for those segments.
  const typeFallbackSec = _secPerKmForType(workout.type, paces);
  let segKm = 0;
  let segMin = 0;
  for (const s of segs) {
    // Only use zone-aware pricing for running workouts. For strength /
    // mobility / other we keep segments raw (no time→km conversion) so a
    // "30 min strength" segment doesn't pretend to be 5 km.
    const v = isRunning
      ? segmentVolume(s, paces, typeFallbackSec)
      : segmentVolume(s, null, 0);
    segKm += v.km;
    segMin += v.minutes;
  }
  const km = Math.max(headline.km, segKm);
  const minutes = Math.max(headline.minutes, segMin);
  // Time-only running workouts: estimate km from minutes so the weekly
  // mileage isn't 0 for a "60 min easy" prescription.
  if (km === 0 && minutes > 0 && isRunning) {
    const sec = _secPerKmForType(workout.type, paces);
    if (sec > 0) {
      return { km: (minutes * 60) / sec, minutes };
    }
  }
  // Distance-only running workouts: estimate minutes from km so the weekly
  // TIME total isn't undercounted (the symmetric flip of the bug above —
  // "12K easy" was contributing 0 min to the denominator).
  if (minutes === 0 && km > 0 && isRunning) {
    const sec = _secPerKmForType(workout.type, paces);
    if (sec > 0) {
      return { km, minutes: (km * sec) / 60 };
    }
  }
  return { km, minutes };
}

/**
 * Sum volume across all workouts in a week.
 */
export function weekVolume(week, paces = null) {
  if (!week) return { minutes: 0, km: 0 };
  const out = { minutes: 0, km: 0 };
  for (const day of week.days || []) {
    for (const wk of day.workouts || []) {
      // Skip "OR" alternatives — only the primary workout counts toward volume
      if (wk.is_alternative) continue;
      // Skip optional/bonus workouts — they're not part of the prescribed plan
      if (wk.is_optional) continue;
      const v = workoutVolume(wk, paces);
      out.minutes += v.minutes;
      out.km += v.km;
    }
  }
  return out;
}

export function formatVolume({ minutes, km }) {
  const parts = [];
  if (km > 0.05) parts.push(`${km.toFixed(km >= 10 ? 0 : 1)} km`);
  if (minutes > 0.5) {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    parts.push(hrs > 0 ? `${hrs}h ${mins}m` : `${mins} min`);
  }
  return parts.join(" · ");
}

/**
 * Build a lookup table from a flat list of workout-logs.
 * Key: `${week_number}-${day_label}-${workout_id}`.
 */
export function logsByKey(logs) {
  const map = {};
  for (const log of logs || []) {
    map[`${log.week_number}-${log.day_label}-${log.workout_id}`] = log;
  }
  return map;
}

/**
 * Per-week planned vs actual totals.
 *   planned: { km, minutes, count } — only counts non-rest workouts
 *   actual:  { km, minutes, complete, partial, skipped, missed, total, completion_pct }
 *   missed = scheduled non-rest workouts with no log AND week is in the past
 *   total  = scheduled non-rest workouts
 */
export function computeWeekActuals(week, logsLookup, opts = {}) {
  const planned = { km: 0, minutes: 0, count: 0 };
  const actual = {
    km: 0,
    minutes: 0,
    complete: 0,
    partial: 0,
    skipped: 0,
    missed: 0,
    total: 0,
    completion_pct: 0,
  };
  if (!week) return { planned, actual };
  const isPast = !!opts.isPast;
  // Caller may pass `racesInWeek` (array of ScheduledRace objects) so race
  // distance + estimated time is added to the planned totals — keeping the
  // "athlete log" row in sync with the headline week volume.
  const racesInWeek = Array.isArray(opts.racesInWeek) ? opts.racesInWeek : [];
  const athletePaces = opts.athletePaces || null;
  for (const day of week.days || []) {
    for (const wo of day.workouts || []) {
      if (!wo || wo.type === "rest") continue;
      // Don't double-count "OR" alternatives — they're a backup choice, not extra work
      if (wo.is_alternative) {
        // Still credit a log against the alternative if the athlete picked it
        const log = (logsLookup || {})[`${week.week_number}-${day.label}-${wo.id}`];
        if (log) {
          if (log.status === "complete") actual.complete += 1;
          else if (log.status === "partial") actual.partial += 1;
          else if (log.status === "skipped") actual.skipped += 1;
          if (log.distance_actual_km) actual.km += Number(log.distance_actual_km) || 0;
          if (log.duration_actual_min) actual.minutes += Number(log.duration_actual_min) || 0;
        }
        continue;
      }
      // Optional/bonus workouts don't add to planned/missed counts, but a
      // completed log still credits actual mileage + completion count.
      if (wo.is_optional) {
        const log = (logsLookup || {})[`${week.week_number}-${day.label}-${wo.id}`];
        if (log) {
          if (log.status === "complete") actual.complete += 1;
          else if (log.status === "partial") actual.partial += 1;
          else if (log.status === "skipped") actual.skipped += 1;
          if (log.distance_actual_km) actual.km += Number(log.distance_actual_km) || 0;
          if (log.duration_actual_min) actual.minutes += Number(log.duration_actual_min) || 0;
        }
        continue;
      }
      planned.count += 1;
      actual.total += 1;
      const v = workoutVolume(wo, athletePaces);
      planned.minutes += v.minutes;
      planned.km += v.km;
      const log = (logsLookup || {})[`${week.week_number}-${day.label}-${wo.id}`];
      if (!log) {
        if (isPast) actual.missed += 1;
        continue;
      }
      if (log.status === "complete") actual.complete += 1;
      else if (log.status === "partial") actual.partial += 1;
      else if (log.status === "skipped") actual.skipped += 1;
      if (log.distance_actual_km) actual.km += Number(log.distance_actual_km) || 0;
      if (log.duration_actual_min) actual.minutes += Number(log.duration_actual_min) || 0;
    }
  }
  // Add scheduled-race contributions to planned (matches the headline volume).
  for (const r of racesInWeek) {
    const parsed = parseRaceDistance(r.distance || r.name, athletePaces);
    if (parsed.km > 0) {
      planned.km += parsed.km;
      planned.minutes += parsed.minutes;
    }
  }
  if (planned.count > 0) {
    // Completion = complete + 0.5 × partial relative to scheduled.
    actual.completion_pct = (actual.complete + 0.5 * actual.partial) / planned.count;
  }
  return { planned, actual };
}

/**
 * Whether a week has already started (in the past or current).
 * Uses program.start_date + week.week_number to derive the week start.
 * Returns true if week_start <= today.
 */
export function isWeekStarted(program, week) {
  if (!program?.start_date || !week?.week_number) return false;
  try {
    const start = new Date(program.start_date);
    if (isNaN(start.getTime())) return false;
    const wkStart = new Date(start);
    wkStart.setDate(wkStart.getDate() + (Number(week.week_number) - 1) * 7);
    return wkStart.getTime() <= Date.now();
  } catch {
    return false;
  }
}

/**
 * Whether a week ended fully (week_end < today).
 */
export function isWeekPast(program, week) {
  if (!program?.start_date || !week?.week_number) return false;
  try {
    const start = new Date(program.start_date);
    if (isNaN(start.getTime())) return false;
    const wkEnd = new Date(start);
    wkEnd.setDate(wkEnd.getDate() + Number(week.week_number) * 7);
    return wkEnd.getTime() <= Date.now();
  } catch {
    return false;
  }
}

/**
 * Aggregate planned/actuals across all started weeks.
 * Future weeks (week_start > today) are excluded.
 */
export function computeProgramActuals(program, logsLookup, opts = {}) {
  const total = {
    planned: { km: 0, minutes: 0, count: 0 },
    actual: {
      km: 0,
      minutes: 0,
      complete: 0,
      partial: 0,
      skipped: 0,
      missed: 0,
      total: 0,
      completion_pct: 0,
    },
    by_week: [],
  };
  if (!program) return total;
  // Caller can pass races + paces so race contributions (km + estimated time)
  // roll up into both per-week and overall totals.
  const allRaces = Array.isArray(opts.races) ? opts.races : [];
  const athletePaces = opts.athletePaces || null;
  for (const w of program.weeks || []) {
    if (!isWeekStarted(program, w) && !w.locked /* fallback for old data */) {
      // Skip future weeks entirely.
      // Keep a stub entry so UI can still render the row.
      total.by_week.push({ week_number: w.week_number, future: true });
      continue;
    }
    if (w.locked) {
      total.by_week.push({ week_number: w.week_number, future: true });
      continue;
    }
    const past = isWeekPast(program, w);
    const [ws, we] = weekDateRange(program.start_date, w.week_number);
    const racesInWeek = allRaces.filter(
      (r) => r.date && ws && we && r.date >= ws && r.date <= we
    );
    const stats = computeWeekActuals(w, logsLookup, {
      isPast: past,
      racesInWeek,
      athletePaces,
    });
    total.planned.km += stats.planned.km;
    total.planned.minutes += stats.planned.minutes;
    total.planned.count += stats.planned.count;
    total.actual.km += stats.actual.km;
    total.actual.minutes += stats.actual.minutes;
    total.actual.complete += stats.actual.complete;
    total.actual.partial += stats.actual.partial;
    total.actual.skipped += stats.actual.skipped;
    total.actual.missed += stats.actual.missed;
    total.actual.total += stats.actual.total;
    total.by_week.push({ week_number: w.week_number, ...stats, past });
  }
  if (total.planned.count > 0) {
    total.actual.completion_pct =
      (total.actual.complete + 0.5 * total.actual.partial) / total.planned.count;
  }
  return total;
}

/**
 * Group consecutive weeks sharing the same phase_label for nav / PDF grouping.
 * Returns array of { phase_label, weeks: [{index, week}] }.
 * Weeks with no phase_label get grouped under phase_label "" (unlabeled).
 */
export function groupWeeksByPhase(weeks) {
  if (!weeks || weeks.length === 0) return [];
  const groups = [];
  let current = null;
  weeks.forEach((w, idx) => {
    const label = (w.phase_label || "").trim();
    if (!current || current.phase_label !== label) {
      current = { phase_label: label, weeks: [] };
      groups.push(current);
    }
    current.weeks.push({ index: idx, week: w });
  });
  return groups;
}

// ---- Strength helpers ----

export const STRENGTH_BLOCK_KINDS = [
  { value: "regular", label: "Straight sets" },
  { value: "superset", label: "Superset" },
  { value: "circuit", label: "Circuit" },
  { value: "amrap", label: "AMRAP" },
  { value: "emom", label: "EMOM" },
  { value: "for_time", label: "For time" },
];

export const STRENGTH_BLOCK_KIND_MAP = Object.fromEntries(
  STRENGTH_BLOCK_KINDS.map((k) => [k.value, k])
);

export function strengthBlockLabel(kind) {
  return STRENGTH_BLOCK_KIND_MAP[kind]?.label || kind;
}

export function mkStrengthExercise(overrides = {}) {
  return {
    id: newId(),
    name: "",
    sets: "3",
    reps: "8-12",
    intensity_kind: null, // "rpe" | "rir" | null — optional
    rpe: "", // value (e.g. "7" or "2")
    tempo: "",
    rest: "",
    notes: "",
    alternatives: [], // optional list of swap names the athlete can pick
    ...overrides,
  };
}

export function mkStrengthBlock(overrides = {}) {
  return {
    id: newId(),
    kind: "regular",
    label: "",
    rounds: null,
    time_cap_minutes: null,
    notes: "",
    exercises: [mkStrengthExercise()],
    ...overrides,
  };
}

export function defaultStrengthBlocks() {
  return [
    mkStrengthBlock({
      kind: "regular",
      label: "A",
      exercises: [
        mkStrengthExercise({ name: "", sets: "3", reps: "8-10" }),
      ],
    }),
  ];
}

/**
 * Format strength-exercise intensity for display.
 * Returns "RPE 7" / "RIR 2" / "" depending on kind + value.
 * Backwards-compat: if kind is null but value exists, assume RPE.
 */
export function formatStrengthIntensity(ex) {
  if (!ex) return "";
  const val = (ex.rpe || "").toString().trim();
  if (!val) return "";
  const kind = ex.intensity_kind || "rpe";
  return `${kind.toUpperCase()} ${val}`;
}
